import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { ICancelationService } from '@shared/interfaces/cancelation-service.interface';
import { IDepartment } from '@shared/interfaces/deparment.interface';
import { IMunicipality } from '@shared/interfaces/municipality.interface';
import { IParametersConfig } from '@shared/interfaces/parameters-config.interface';
import { IServiceDetail, IServiceType, IStatusService } from '@shared/interfaces/service-detail.interface';
import { ApiResponse } from '@shared/interfaces/wrap-api-response.interface';
import { catchError, finalize, forkJoin, of, switchMap, tap } from 'rxjs';
import { DisabilityType } from '../data-access/interfaces/disabilitie-interface';
import { IGenerateTransactionPaymentDto } from '../data-access/interfaces/generate-transaction-payment';
import { DevicePosition } from '../data-access/interfaces/map-interface';
import { IPaymentInfo } from '../data-access/interfaces/payment_interface';
import { RecommendedVehicleModel } from '../data-access/interfaces/recommended-vehicle.interface';
import { UbicacionGeneral } from '../data-access/interfaces/search-filter';
import {
	ApiResponseList,
	AutocompletePrediction,
	ServiceFilters,
	Servicio
} from '../data-access/interfaces/services-interface';
import { ServiceStep } from '../data-access/interfaces/stop-interfaces';
import { ServiceCostSummary } from '../data-access/interfaces/summary-cost-interface';
import { ServiceTimelineStatus } from '../data-access/interfaces/timeline-status';
import { TripType } from '../data-access/interfaces/trip-type.interface';
import { ServicesService } from '../data-access/services.service';
import { CouponService } from '../../coupon/data-access/coupon.service';
import { Coupon } from '../../coupon/data-access/interfaces/coupon.interface';
import { AddressDetails } from '../ui/stop/stop.component';
import { ITrip } from '@shared/interfaces/trip.interface';
import { People } from '@shared/interfaces/people.interface';
import { PaginatorResult } from '@shared/interfaces/paginator_interface';

export interface CreateFormState {
	tripType: 'oneWay' | 'roundTrip';
	passengerCount: number;
	selectedServiceTypeId: number | null;
	selectedDisabilityId: number | null;
	selectedOrigin: AutocompletePrediction | null;
	selectedDestination: AutocompletePrediction | null;
	originDetails: AddressDetails | null;
	destinationDetails: AddressDetails | null;
	serviceDate: Date | null;
	serviceTime: Date | null;
	returnDate: Date | null;
	returnTime: Date | null;
	stops: { details: AddressDetails | null; time: number }[];
	totalDistance: number | null;
	totalDuration: number | null;
	totalDistanceText: string | null;
	totalDurationText: string | null;
}
export interface ServicesState {
	allServices: Servicio[];
	serviceStatuses: IStatusService[];
	serviceTypes: IServiceType[];
	serviceTimeline: ServiceTimelineStatus[];
	devicePosition: DevicePosition | null;
	disabilityTypes: DisabilityType[];
	finishedServices: IServiceDetail[];
	serviceSteps: ServiceStep[];
	recommendedVehicleModels: RecommendedVehicleModel[];
	filters: Partial<ServiceFilters>;
	selectedServiceId: number | null;
	selectedServiceDetail: IServiceDetail | null;
	isLoading: boolean;
	isListLoading: boolean;
	isDetailLoading: boolean;
	hasFiltered: boolean;
	isLoadingRecommendedVehicles: boolean;
	serviceCreationSuccess: boolean | null;
	serviceCreationError: string | null;
	selectedVehicleModelId: number | null;
	choosingVehicleModel: boolean;
	serviceCostSummary: ServiceCostSummary | null;
	isLoadingCostSummary: boolean;
	serviceCostSummaryError: string | null;
	chooseVehicleModelSuccess: boolean | null;
	chooseVehicleModelError: string | null;
	createFormState: CreateFormState | null;
	isServiceAlreadyCreated: boolean;
	departments: IDepartment[];
	municipalities: IMunicipality[];
	isLoadingDepartments: boolean;
	isLoadingMunicipalities: boolean;
	departmentsError: string | null;
	municipalitiesError: string | null;
	combinedLocations: UbicacionGeneral[];
	noCostsAvailable: boolean;
	pendingRatings: PaginatorResult<ITrip>;
	isLoadingPendingRatings: boolean;
	tripTypes: TripType[];
	paymentInfo: IPaymentInfo[] | null;
	isLoadingPaymentInfo: boolean;
	paymentInfoError: string | null;
	isLoadingTransactionPayment: boolean;
	transactionPayment: IGenerateTransactionPaymentDto | null;
	transactionPaymentError: string | null;
	maxHoursToRequestService: IParametersConfig | null;
	serviceCreated: { id: number; transactionId: string } | null;
	serviceCancellation: ICancelationService[] | null;
	isLoadingCancellationReasons: boolean;
	serviceBeneficiary: People[];
	isLoadingServiceBeneficiary: boolean;
	coupon: Coupon | null;
	isLoadingCoupon: boolean;
}

const initialState: ServicesState = {
	allServices: [],
	filters: {},
	serviceTypes: [],
	finishedServices: [],
	tripTypes: [],
	serviceTimeline: [],
	serviceSteps: [],
	devicePosition: null,
	disabilityTypes: [],
	selectedServiceDetail: null,
	serviceStatuses: [],
	recommendedVehicleModels: [],
	selectedServiceId: null,
	isLoading: false,
	isListLoading: false,
	isDetailLoading: false,
	hasFiltered: false,
	isLoadingRecommendedVehicles: false,
	serviceCostSummary: null,
	isLoadingCostSummary: false,
	serviceCostSummaryError: null,
	serviceCreationSuccess: null,
	serviceCreationError: null,
	selectedVehicleModelId: null,
	choosingVehicleModel: false,
	chooseVehicleModelSuccess: null,
	chooseVehicleModelError: null,
	createFormState: null,
	isServiceAlreadyCreated: false,
	departments: [],
	municipalities: [],
	isLoadingDepartments: false,
	isLoadingMunicipalities: false,
	departmentsError: null,
	municipalitiesError: null,
	combinedLocations: [],
	noCostsAvailable: false,
	pendingRatings: { data: [], meta: { page: 1, pageSize: 10, itemCount: 0 } },
	isLoadingPendingRatings: false,
	paymentInfo: null,
	isLoadingPaymentInfo: false,
	paymentInfoError: null,
	isLoadingTransactionPayment: false,
	transactionPayment: null,
	transactionPaymentError: null,
	maxHoursToRequestService: null,
	serviceCreated: null,
	serviceCancellation: [],
	isLoadingCancellationReasons: false,
	serviceBeneficiary: [],
	isLoadingServiceBeneficiary: false,
	coupon: null,
	isLoadingCoupon: false
};

export const ServicesStore = signalStore(
	{ providedIn: 'root' },
	withState(initialState),
	withComputed(
		({
			allServices,
			selectedServiceDetail,
			isLoadingRecommendedVehicles,
			recommendedVehicleModels,
			isListLoading,
			serviceCreationSuccess,
			serviceCreationError,
			selectedVehicleModelId,
			choosingVehicleModel,
			chooseVehicleModelSuccess,
			serviceCostSummary,
			isLoadingCostSummary,
			serviceCostSummaryError,
			chooseVehicleModelError,
			createFormState,
			isServiceAlreadyCreated,
			departments,
			municipalities,
			isLoadingDepartments,
			isLoadingMunicipalities,
			departmentsError,
			municipalitiesError,
			noCostsAvailable,
			pendingRatings,
			isLoadingPendingRatings,
			tripTypes,
			serviceBeneficiary,
			isLoadingServiceBeneficiary,
			coupon,
			isLoadingCoupon
		}) => ({
			filteredServices: computed(() => allServices()),
			serviceDetail: computed(() => selectedServiceDetail()),
			getRecommendedVehicleModels: computed(() => recommendedVehicleModels()),
			areRecommendedVehiclesLoading: computed(() => isLoadingRecommendedVehicles()),
			isServiceCreationSuccessful: computed(() => serviceCreationSuccess()),
			getServiceCreationError: computed(() => serviceCreationError()),
			isCreatingService: computed(() => serviceCreationSuccess() === null && isListLoading()),
			getSelectedVehicleModelId: computed(() => selectedVehicleModelId()),
			isChoosingVehicleModel: computed(() => choosingVehicleModel()),
			getServiceCostSummary: computed(() => serviceCostSummary()),
			isServiceCostSummaryLoading: computed(() => isLoadingCostSummary()),
			getServiceCostSummaryError: computed(() => serviceCostSummaryError()),
			getChooseVehicleModelSuccess: computed(() => chooseVehicleModelSuccess()),
			getChooseVehicleModelError: computed(() => chooseVehicleModelError()),
			getCreateFormState: computed(() => createFormState()),
			getIsServiceAlreadyCreated: computed(() => isServiceAlreadyCreated()),
			getDepartments: computed(() => departments()),
			getMunicipalities: computed(() => municipalities()),
			areDepartmentsLoading: computed(() => isLoadingDepartments()),
			areMunicipalitiesLoading: computed(() => isLoadingMunicipalities()),
			getDepartmentsError: computed(() => departmentsError()),
			getMunicipalitiesError: computed(() => municipalitiesError()),
			getCombinedLocations: computed(() => {
				const deps: UbicacionGeneral[] = departments().map((d) => ({ ...d, tipo: 'departamento' }));
				const munis: UbicacionGeneral[] = municipalities().map((m) => ({ ...m, tipo: 'municipio' }));
				return [...deps, ...munis];
			}),
			areCombinedLocationsLoading: computed(() => isLoadingDepartments() || isLoadingMunicipalities()),
			getCombinedLocationsError: computed(() => departmentsError() || municipalitiesError()),
			getNoCostsAvailable: computed(() => noCostsAvailable()),
			getPendingRatings: computed(() => pendingRatings()),
			arePendingRatingsLoading: computed(() => isLoadingPendingRatings()),
			getTripTypes: computed(() => tripTypes()),
			getServiceBeneficiary: computed(() => serviceBeneficiary()),
			areServiceBeneficiaryLoading: computed(() => isLoadingServiceBeneficiary()),
			getCoupon: computed(() => coupon()),
			isCouponLoading: computed(() => isLoadingCoupon())
		})
	),

	withMethods((store, servicesService = inject(ServicesService), couponService = inject(CouponService)) => ({
		loadTripTypes(): void {
			patchState(store, { tripTypes: [] });
			servicesService
				.getTripTypes()
				.pipe(
					tap((response) => {
						patchState(store, { tripTypes: response.result });
					})
				)
				.subscribe();
		},
		loadPendingRatings(page?: number, pageSize?: number): void {
			patchState(store, { isLoadingPendingRatings: true });

			servicesService
				.getPendingRatings(page ?? 1, pageSize ?? 5)
				.pipe(
					finalize(() => patchState(store, { isLoadingPendingRatings: false })),
					tap((response) => {
						console.log('response:', response);
						if (response.data) {
							patchState(store, { pendingRatings: response });
						}
					}),
					catchError((error) => {
						console.error('Store Error: Failed to load pending ratings', error);
						return of(null);
					})
				)
				.subscribe();
		},

		selectService(id: number | null): void {
			patchState(store, { selectedServiceId: id });
			if (id) {
				this.fetchServiceCurrentSelectedById(Number(id));
				this.loadServiceCostSummary(Number(id));
				this.loadPaymentInfo(Number(id));
			} else {
				patchState(store, { selectedServiceDetail: null, serviceCostSummary: null, paymentInfo: null });
			}
		},
		resetChooseVehicleModelStatus(): void {
			patchState(store, { chooseVehicleModelSuccess: null, chooseVehicleModelError: null });
		},
		loadLocations(query: string): void {
			patchState(store, {
				isLoadingDepartments: true,
				isLoadingMunicipalities: true,
				departmentsError: null,
				municipalitiesError: null,
				departments: [],
				municipalities: [],
				combinedLocations: []
			});
			servicesService
				.getDepartments(query)
				.pipe(
					finalize(() => patchState(store, { isLoadingDepartments: false })),
					tap((response) => {
						if (response.resultCode === 0 && response.result) {
							patchState(store, { departments: response.result });
						} else {
							patchState(store, { departments: [] });
						}
					}),
					catchError((error) => {
						patchState(store, {
							departments: [],
							departmentsError: error.error?.message || error.message || 'Error de comunicación.'
						});
						return of(null);
					})
				)
				.subscribe();
			servicesService
				.getMunicipalities(query)
				.pipe(
					finalize(() => patchState(store, { isLoadingMunicipalities: false })),
					tap((response) => {
						if (response.resultCode === 0 && response.result) {
							patchState(store, { municipalities: response.result });
						} else {
							patchState(store, { municipalities: [] });
						}
					}),
					catchError((error) => {
						patchState(store, {
							municipalities: [],
							municipalitiesError: error.error?.message || error.message || 'Error de comunicación.'
						});
						return of(null);
					})
				)
				.subscribe();
		},
		clearLocationData(): void {
			patchState(store, {
				departments: [],
				municipalities: [],
				combinedLocations: [],
				departmentsError: null,
				municipalitiesError: null,
				isLoadingDepartments: false,
				isLoadingMunicipalities: false
			});
		},
		fetchServiceCurrentSelectedById(serviceId: number): void {
			patchState(store, { isDetailLoading: true });
			servicesService
				.getServiceDetail(serviceId)
				.pipe(
					finalize(() => patchState(store, { isDetailLoading: false })),
					switchMap((response) => {
						const detail = structuredClone(response.result);
						patchState(store, {
							selectedServiceDetail: detail
						});
						this.loadServiceTimeline(serviceId);
						this.loadServiceSteps(serviceId);
						this.loadDevicePosition(serviceId);
						if (response.result.statusId === 4) {
							this.loadCancellationReasons(serviceId);
						}

						// Si NO es empresarial, ejecutamos los cálculos de costo y pago
						if (detail.isEnterprise == false) {
							return forkJoin([this.loadServiceCostSummary(detail.id), this.loadPaymentInfo(detail.id)]);
						}
						if (detail.isEnterprise == true) {
							return forkJoin([this.loadServiceBeneficiary(detail.id)]);
						}

						return of(null);
					})
				)
				.subscribe();
		},

		/**
		 * Updates a specific service in the allServices array
		 * Calls the detail endpoint and updates only the matching service by ID
		 */
		updateServiceInList(serviceId: number): void {
			servicesService
				.getServiceDetail(serviceId)
				.pipe(
					tap((response) => {
						if (response.result) {
							const currentServices = store.allServices();
							// Find the service index in the array
							const serviceIndex = currentServices.findIndex((service) => service.id === serviceId);

							if (serviceIndex !== -1) {
								// Create a copy of the array with the updated service
								const updatedServices = [...currentServices];

								// Update the service at the found position
								// Keep the service list structure and update relevant fields
								updatedServices[serviceIndex] = {
									...updatedServices[serviceIndex],
									statusId: response.result.statusId,
									status: response.result.status
								};

								// Update the state with the modified array
								patchState(store, { allServices: updatedServices });
								console.log(`✅ Service ${serviceId} updated in list`);
							} else {
								console.warn(`⚠️ Service ${serviceId} not found in allServices`);
							}
						}
					}),
					catchError((error) => {
						console.error(`❌ Error updating service ${serviceId} in list:`, error);
						return of(null);
					})
				)
				.subscribe();
		},

		/**
		 * Adds a new service to the allServices array if it doesn't exist
		 * Calls the detail endpoint and adds the service to the list
		 */
		addServiceInList(serviceId: number): void {
			servicesService
				.getServiceDetail(serviceId)
				.pipe(
					tap((response) => {
						if (response.result) {
							const currentServices = store.allServices();
							// Check if the service already exists in the array
							const exists = currentServices.some((service) => service.id === serviceId);

							if (!exists) {
								// Create a new service object from the detail response
								const newService: Servicio = {
									id: response.result.id,
									originAddress: response.result.originAddress,
									originLat: response.result.originLat,
									originLong: response.result.originLong,
									destinationAddress: response.result.destinationAddress,
									destinationLat: response.result.destinationLat,
									destinationLong: response.result.destinationLong,
									departureDate: response.result.departureDate,
									returnDate: response.result.returnDate,
									statusId: response.result.statusId,
									status: response.result.status,
									isEnterprise: response.result.isEnterprise ?? false
								};

								// Add the new service at the beginning of the array
								const updatedServices = [newService, ...currentServices];

								// Update the state with the new array
								patchState(store, { allServices: updatedServices });
								console.log(`✅ Service ${serviceId} added to list`);
							} else {
								console.log(`ℹ️ Service ${serviceId} already exists in list, skipping...`);
							}
						}
					}),
					catchError((error) => {
						console.error(`❌ Error adding service ${serviceId} to list:`, error);
						return of(null);
					})
				)
				.subscribe();
		},

		loadCoupon(couponId: number): void {
			patchState(store, { isLoadingCoupon: true, coupon: null });
			couponService
				.getById(couponId)
				.pipe(
					finalize(() => patchState(store, { isLoadingCoupon: false })),
					tap((response) => {
						if (response.result) {
							patchState(store, { coupon: response.result });
						}
					}),
					catchError((error) => {
						console.error('Store Error: Failed to load coupon', error);
						return of(null);
					})
				)
				.subscribe();
		},
		loadServiceCostSummary(serviceId: number): void {
			patchState(store, {
				isLoadingCostSummary: true,
				serviceCostSummaryError: null,
				serviceCostSummary: null,
				noCostsAvailable: false,
				coupon: null
			});
			servicesService
				.getServiceCostSummary(serviceId)
				.pipe(
					finalize(() => patchState(store, { isLoadingCostSummary: false })),
					tap((response) => {
						if (response.resultCode === 0 && response.result) {
							patchState(store, { serviceCostSummary: response.result });
							if (response.result.couponId) {
								this.loadCoupon(response.result.couponId);
							}
						} else {
							patchState(store, { noCostsAvailable: true });
						}
					})
				)
				.subscribe();
		},
		loadServiceTimeline(serviceId: number): void {
			servicesService
				.getServiceTimeline(serviceId)
				.pipe(
					tap((response) => {
						patchState(store, { serviceTimeline: response.result });
					})
				)
				.subscribe();
		},
		loadDevicePosition(serviceId: number): void {
			servicesService
				.getDeviceCurrentPosition(serviceId)
				.pipe(
					tap((response) => {
						console.log('Respuesta completa de la API:', response);
						patchState(store, { devicePosition: response.result });
					})
				)
				.subscribe();
		},
		updateVehiclePosition(latitude: number, longitude: number): void {
			const currentPosition = store.devicePosition();
			if (currentPosition) {
				patchState(store, {
					devicePosition: {
						...currentPosition,
						vehicle: {
							latitude,
							longitude
						}
					}
				});
			}
		},
		loadServiceSteps(serviceId: number): void {
			servicesService
				.getServiceSteps(serviceId)
				.pipe(
					tap((response) => {
						patchState(store, { serviceSteps: response.result });
					})
				)
				.subscribe();
		},
		cancelService(): void {
			const serviceId = store.selectedServiceId();
			if (!serviceId) return;
			patchState(store, { isListLoading: true });

			servicesService
				.cancelService(Number(serviceId))
				.pipe(
					finalize(() => patchState(store, { isListLoading: false })),
					tap(() => {
						this.loadServices(store.filters());
						servicesService.getServiceDetail(Number(serviceId)).subscribe({
							next: (detailResponse) => {
								patchState(store, {
									selectedServiceDetail: detailResponse.result
								});
							}
						});
					})
				)
				.subscribe();
		},
		applyFilters(newFilters: Partial<ServiceFilters>): void {
			patchState(store, { filters: newFilters });
			this.loadServices(newFilters);
		},
		loadServiceStatuses(): void {
			patchState(store, { isListLoading: true });
			servicesService
				.getStatusServices()
				.pipe(
					finalize(() => patchState(store, { isListLoading: false })),
					tap((response) => {
						console.log('response:', response);
						patchState(store, {
							serviceStatuses: response.result
						});
					})
				)
				.subscribe();
		},
		loadServices(filters: Partial<ServiceFilters> = {}): void {
			patchState(store, { isListLoading: true });

			servicesService
				.getServices(filters)
				.pipe(
					tap((response) => {
						patchState(store, {
							allServices: response.result,
							isListLoading: false
						});
					})
				)
				.subscribe();
		},
		loadServiceTypes(): void {
			servicesService
				.getServiceTypes()
				.pipe(
					tap((response) => {
						patchState(store, { serviceTypes: response.result });
					})
				)
				.subscribe();
		},
		loadDisabilityTypes(): void {
			servicesService
				.getDisabilityTypes()
				.pipe(
					tap((response) => {
						patchState(store, { disabilityTypes: response.result });
					})
				)
				.subscribe();
		},

		resetServiceCreationStatus(): void {
			patchState(store, { serviceCreationSuccess: null, serviceCreationError: null });
		},
		loadFinishedServices(filters: Partial<ServiceFilters> = {}): void {
			patchState(store, { isListLoading: true });
			servicesService
				.getFinishedServices(filters)
				.pipe(
					tap((response) => {
						patchState(store, {
							finishedServices: response.data,
							isListLoading: false // <--- Muévelo aquí
						});
					})
				)
				.subscribe();
		},
		saveCreateFormState(state: CreateFormState) {
			patchState(store, { createFormState: state });
		},
		clearCreateFormState() {
			patchState(store, {
				createFormState: null,
				isServiceAlreadyCreated: false,
				selectedServiceId: null,
				selectedVehicleModelId: null,
				chooseVehicleModelSuccess: null,
				chooseVehicleModelError: null,
				recommendedVehicleModels: []
			});
		},
		clearSelectedServiceInfo() {
			patchState(store, {
				selectedServiceId: null,
				selectedVehicleModelId: null,
				chooseVehicleModelSuccess: null,
				chooseVehicleModelError: null,
				recommendedVehicleModels: []
			});
		},
		loadPaymentInfo(serviceId: number): void {
			patchState(store, { isLoadingPaymentInfo: true, paymentInfoError: null });
			servicesService
				.getPaymentInfo(serviceId)
				.pipe(
					finalize(() => patchState(store, { isLoadingPaymentInfo: false })),
					tap((response: ApiResponseList<IPaymentInfo>) => {
						if (response.resultCode === 0 && response.result) {
							patchState(store, { paymentInfo: response.result });
						} else {
							patchState(store, { paymentInfo: [], paymentInfoError: 'No se encontró información de pago.' });
						}
					}),
					catchError((error) => {
						console.error('Store Error: Failed to load payment info', error);
						patchState(store, {
							paymentInfo: null,
							paymentInfoError: error.error?.message || error.message || 'Error de comunicación.'
						});
						return of(null);
					})
				)
				.subscribe();
		},
		/*
		 * Method to generate link transaction payment
		 */
		generateTransactionPayment(serviceId: number): void {
			patchState(store, { isLoadingTransactionPayment: true });
			servicesService
				.generateTransactionPayment(serviceId)
				.pipe(
					finalize(() => patchState(store, { isLoadingTransactionPayment: false })),
					tap((response: ApiResponse<IGenerateTransactionPaymentDto>) => {
						if (response.result) {
							patchState(store, { transactionPayment: response.result });
						}
					}),
					catchError((error) => {
						console.error('Store Error: Failed to generate transaction payment', error);
						patchState(store, {
							transactionPayment: null,
							transactionPaymentError: error.error?.message || error.message || 'Error de comunicación.'
						});
						return of(null);
					})
				)

				.subscribe();
		},
		loadParameter(parameterCode: string): void {
			servicesService
				.getParameterConfig(parameterCode)
				.pipe(
					tap((response) => {
						switch (parameterCode) {
							case 'max_hours_to_request_service':
								patchState(store, { maxHoursToRequestService: response.result });
								break;
						}
					})
				)
				.subscribe();
		},
		clearTransactionPayment(): void {
			patchState(store, { transactionPayment: null, transactionPaymentError: null });
		},
		loadCancellationReasons(serviceId: number): void {
			patchState(store, { serviceCancellation: [], isLoadingCancellationReasons: true });
			servicesService
				.getCancellationReasons(serviceId)
				.pipe(
					finalize(() => patchState(store, { isLoadingCancellationReasons: false })),
					tap((response) => {
						patchState(store, { serviceCancellation: response.result, isLoadingCancellationReasons: false });
					})
				)
				.subscribe();
		},
		loadServiceBeneficiary(serviceId: number): void {
			patchState(store, { isLoadingServiceBeneficiary: true });
			servicesService
				.getServiceBeneficiary(serviceId)
				.pipe(
					finalize(() => patchState(store, { isLoadingServiceBeneficiary: false })),
					tap((response) => {
						if (response.resultCode === 0 && response.result) {
							patchState(store, { serviceBeneficiary: response.result });
						}
					}),
					catchError((error) => {
						console.error('Store Error: Failed to load service beneficiary', error);
						patchState(store, { serviceBeneficiary: [] });
						return of(null);
					})
				)
				.subscribe();
		},
		resetState(): void {
			patchState(store, initialState);
		}
	})),

	withHooks({
		onInit({
			loadServiceTypes,
			loadServiceStatuses,
			loadServices,
			loadDisabilityTypes,
			loadPendingRatings,
			loadTripTypes
		}) {
			loadServiceStatuses();
			loadServiceTypes();
			loadServices();
			loadTripTypes();
			loadDisabilityTypes();
			loadPendingRatings();
		}
	})
);
